import _ from "lodash";
import axios from "../../lib/apiAxios";
import moment from "moment";
import { nextTick } from "vue";

import config from "../../config";
import { fetchRequestForEdit } from "@/services/api.services";
import { fetchDropdownValues } from "@/services/benchling/benchlingAPI";
import { createPlateLayout, findAvailableWell } from "@/services/plate.service";
import { EventBus } from "@/lib/event-bus";
import { enqueueBackgroundTaskAndPoll } from "@/lib/backgroundTasks";

/******************************************************************
 * STATE
 ******************************************************************/
const state = {
    activeCondition: undefined, // The condition/sample selected on the left, undefined if nothing is selected
    plateWellSelection: [],
    sampleConditionSelection: [],
    sampleEditSelection: [],
    conditionMetadata: undefined,
    activePlate: 0,
    collectionDate: undefined,
    container: undefined,
    experiment: undefined,
    initialized: false,
    isProcessing: false, // Show spinner next to add button
    isSaving: false,
    protocols: [],
    plates: [],
    existingPlates: [],
    request: {},
    sampleDescriptors: [],
    sampleTypes: [],
    entityKeys: [],
    isSamplesBool: false,
};

/******************************************************************
 * GETTERS
 ******************************************************************/
const getters = {
    plateWellSelection: (state) => state.plateWellSelection,
    sampleEditSelection: (state) => state.sampleEditSelection,
    activePlate: (state) => state.activePlate,
    activeCondition: (state) => state.activeCondition,
    conditionMetadata: (state) => state.conditionMetadata,
    sampleConditionSelection: (state) => state.sampleConditionSelection,
    collectionDate: (state) =>
        state.collectionDate ? state.collectionDate : moment().format("YYYY-MM-DD"),
    container: (state) => state.container,
    experiment: (state) => state.experiment,
    initialized: (state) => state.initialized,
    isMultiAssign: (state) => {
        return state.container && state.container.multi_assign;
    },
    isPlateValid: (state) => {
        // Loop through each well to check if at least one condition has been assigned to a well
        if (state.plates.length === 0) {
            return false;
        }
        const layout = state.plates[state.activePlate].layout;
        if (state.initialized && layout.length > 0) {
            for (let rowIndex = 0; rowIndex < layout.length; rowIndex++) {
                const row = layout[rowIndex];
                for (let colIndex = 0; colIndex < row.length; colIndex++) {
                    const col = row[colIndex];
                    if (col.condition) {
                        return true;
                    }
                }
            }
        }
        return false;
    },
    isProcessing: (state) => state.isProcessing,
    isSaving: (state) => state.isSaving,
    isTubeSeries: (state) => {
        return state.initialized && state.container.rows === 1 && state.container.columns === 1;
    },
    hasActiveCondition: (state) => !!state.activeCondition,
    hasAssignments: (state, getters) => {
        if (getters.isTubeSeries && getters.layout.length > 0) {
            return !!getters.layout[0].length;
        }
    },
    layout: (state) =>
        state.plates[state.activePlate] ? state.plates[state.activePlate].layout : [],
    plates: (state) => state.plates,
    protocols: (state) => state.protocols,
    request: (state) => state.request,
    sampleDescriptors: (state) => {
        if (
            state.experiment &&
            state.experiment.sample_descriptor_list &&
            state.experiment.sample_descriptor_list.length > 0
        ) {
            return _.filter(state.sampleDescriptors, (descriptor) => {
                return (
                    _.indexOf(state.experiment.sample_descriptor_list || [], descriptor.name) > -1
                );
            });
        } else {
            return [];
        }
    },
    sampleTypes: (state) => {
        if (
            state.experiment &&
            state.experiment.material_type_list &&
            state.experiment.material_type_list.length > 0
        ) {
            return _.filter(state.sampleTypes, (type) => {
                return _.indexOf(state.experiment.material_type_list || [], type.name) > -1;
            });
        } else {
            return state.sampleTypes;
        }
    },
    entityKeys: (state) => {
        if (state.protocols.length > 0) {
            return state.protocols[0].entity_keys;
        }
    },
    subwell: (state) => {
        if (state.experiment && state.experiment.subwell) {
            return state.experiment.subwell;
        }
    },
    selectionHasCondition: (state) => {
        let hasCondition = false;

        if (state.plates[state.activePlate]) {
            const activePlateArray = state.plates[state.activePlate].layout.flat();
            state.plateWellSelection.forEach((wellId) => {
                if (activePlateArray.find((well) => well.id === wellId).condition) {
                    hasCondition = true;
                }
            });
        }
        return hasCondition;
    },
    editedWellsArray: (state) => {
        let editedArray = [];
        if (state.existingPlates[state.activePlate]) {
            const existingArray = state.existingPlates[state.activePlate].layout.flat();
            const activePlateArray = state.plates[state.activePlate].layout.flat();
            editedArray = activePlateArray.map((well, index) => {
                return existingArray[index].condition && !_.isEqual(well, existingArray[index])
                    ? well.id
                    : null;
            });
        }
        return editedArray;
    },
};

/******************************************************************
 * ACTIONS
 ******************************************************************/
const actions = {
    /**
     * Initializes the create plate page when user press the CreateDialog proceed button
     */
    async initialize({ commit, getters }, data) {
        const descriptorsResponse = await fetchDropdownValues("Sample descriptor");
        const typesResponse = await fetchDropdownValues("Material Type");
        data.sampleDescriptors = descriptorsResponse;
        data.sampleTypes = typesResponse;
        commit("initializeState", data);

        if (!getters.isMultiAssign && state.plates.length === 0) {
            commit("addTab");
        }
        commit("closeNavigationDrawer", null, { root: true });
    },

    async edit({ commit, dispatch }, requestCode) {
        // Load data from server
        const editData = await fetchRequestForEdit(requestCode);

        const { collectionDate, container, plates, protocols, request } = editData;
        dispatch("initialize", {
            collectionDate,
            container,
            plates,
            protocols,
            request,
        });
    },

    reset({ commit }) {
        commit("resetState");
        commit("openNavigationDrawer", null, { root: true });
    },

    addTab({ commit }, options) {
        commit("addTab", options);
        commit("clearPlateSelection");
    },

    removeTab({ commit }, index) {
        commit("removeTab", index);
    },

    setActivePlate: ({ commit }, index) => {
        commit("setActivePlate", index);
        commit("clearPlateSelection");
    },

    setActiveCondition({ commit }, activeCondition) {
        commit("setActiveCondition", activeCondition);
    },

    setSampleConditionSelection({ commit }, sampleConditionSelection) {
        commit("setSampleConditionSelection", sampleConditionSelection);
    },

    clearSampleConditions({ commit }) {
        commit("clearSampleConditions");
    },

    clearSampleConditionsAndMetadata({ commit }) {
        commit("clearSampleConditions");
        commit("clearConditionMetadata");
    },

    setExperiment({ commit }, experiment) {
        commit("setExperiment", experiment);
        commit("setActiveCondition", null);
        commit("clearSampleConditions");
        commit("clearConditionMetadata");
    },

    togglePlateSelection({ commit, state }, plateWellSelection) {
        commit("togglePlateSelection", plateWellSelection);
    },

    expandPlateSelection({ commit }, expId) {
        commit("expandPlateSelection", expId);
    },

    clearPlateSelection({ commit }) {
        commit("clearPlateSelection");
    },

    selectAllPlateSelection({ commit }) {
        commit("selectAllPlateSelection");
    },

    assignToSelection({ commit, state, getters }, wellData) {
        getters.layout.flat().forEach((well) => {
            if (state.plateWellSelection.includes(well.id)) {
                commit("setActiveCondition", state.sampleConditionSelection[0]);
                const wellObj = { ...well, ...wellData };
                if (wellData.action === "edit") {
                    commit("editDestinationWell", wellObj);
                } else {
                    commit("assignConditionToDestinationWell", wellObj);
                }
            }
            commit("setConditionMetadata", wellData);
        });
        commit("clearPlateSelection");
    },

    removeFromSelection({ commit, state, getters }) {
        getters.layout.flat().forEach((well) => {
            if (state.plateWellSelection.includes(well.id)) {
                commit("removeConditionFromDestinationWell", well);
            }
        });
        commit("clearPlateSelection");
    },

    /**
     *  Assigns a condition or sample to its destination cell
     *  @param data { row, col } Pointer to destination well by row/col index
     */
    assignConditionToDestinationWell({ commit }, data) {
        if (data && data.hasOwnProperty("row") && data.hasOwnProperty("col")) {
            commit("assignConditionToDestinationWell", data);
        }
    },

    editDestinationWell({ commit }, data) {
        if (data && data.hasOwnProperty("row") && data.hasOwnProperty("col")) {
            commit("editDestinationWell", data);
        }
    },

    assignAllWellsToPlate({ commit }, data) {
        console.log("PCS-assignAllWellsToPlate");
        const container = state.container;
        const packingDirection = data.packingDirection;
        delete data.packingDirection;
        // Store Metadata selections
        commit("setConditionMetadata", data);
        state.sampleConditionSelection.forEach((wellCondition) => {
            let { well, plate } = findAvailableWell(container, state.plates, packingDirection);
            // If plate is full, addTab
            if (!well || !plate) {
                commit("addTab");
                const newPlateAvailableWell = findAvailableWell(
                    container,
                    state.plates,
                    packingDirection
                );
                well = newPlateAvailableWell.well;
                plate = newPlateAvailableWell.plate;
            }
            const condition = Object.assign(wellCondition, data);
            commit("assignConditionToDestinationPlateAndWell", { well, plate, condition });
        });
    },

    assignScannedSampleToPlate({ commit, getters }, data) {
        console.log("PCS-assignScannedSampleToPlate");
        if (getters.isTubeSeries) {
            const selectedProtocols = getters.protocols;
            commit("addSample", { selectedProtocols });
        } else {
            const container = state.container;
            const packingDirection = data;
            let { well, plate } = findAvailableWell(container, state.plates, packingDirection);

            // If plate is full, addTab
            if (!well || !plate) {
                commit("addTab");
                const newPlateAvailableWell = findAvailableWell(
                    container,
                    state.plates,
                    packingDirection
                );
                well = newPlateAvailableWell.well;
                plate = newPlateAvailableWell.plate;
            }
            this.$toast(`Please add Sample ${state.activeCondition.samp} to ${well.id}`, {
                timeout: 5000,
                position: "bottom-left",
            });
            commit("assignConditionToDestinationPlateAndWell", { well, plate });
        }
    },

    autoAssignConditionToDestinationWell({ commit, state }) {
        // Determine the protocols (from sample)
        const container = state.container;
        // Loop though each and find or create plate and then identify next available free well
        for (const protocol of state.activeCondition.protocols) {
            // Only use protocols with functional teams of 'Analytical'
            if (protocol.functionalTeam !== "Analytical") {
                continue;
            }

            // If this is the first plate give it the name of the first protocol
            if (state.plates.length === 1 && !state.plates[0].name) {
                state.plates[0].tabId = protocol.code;
                state.plates[0].name = `${protocol.code} (1)`;
            }
            // Do we have a plate(s) for the protocol already?
            const protocolPlates = _.filter(state.plates, { tabId: protocol.code });
            const { well, plate } = findAvailableWell(container, protocolPlates);
            if (well && plate) {
                this.$toast(`Please add Sample ${state.activeCondition.samp} to ${well.id}`, {
                    timeout: 5000,
                    position: "bottom-left",
                });

                commit("assignConditionToDestinationPlateAndWell", { well, plate });
            } else {
                if (state.activePlate < 18) {
                    const name = `${protocol.code} (${protocolPlates.length + 1})`;
                    commit("addTab", { protocol, name, skipSettingActivePlate: true });
                    const lastPlate = _.last(state.plates);
                    const { well, plate } = findAvailableWell(container, [lastPlate]);
                    this.$toast.success(`Adding new tab for protocol ${protocol.code}`);
                    commit("assignConditionToDestinationPlateAndWell", { well, plate });
                    this.$toast(`Please add Sample ${state.activeCondition.samp} to ${well.id}`, {
                        timeout: 5000,
                        position: "bottom-left",
                    });
                } else {
                    this.$toast.error("Cannot create another Tab. Maximum of 20 is supported");
                }
            }
        }
    },

    removeConditionFromDestinationWell({ commit, state }, data) {
        if (data && data.hasOwnProperty("row") && data.hasOwnProperty("col")) {
            commit("removeConditionFromDestinationWell", data);
        }
    },

    /***************************
     * SAMPLES
     ***************************/
    addSample({ commit, getters }, selectedProtocols) {
        commit("addSample", { selectedProtocols });
    },

    addSelectedSamples({ commit, dispatch }, data) {
        commit("beginProcessing");
        // Handle Batches (i.e one active condition, not array)
        const wells =
            state.sampleConditionSelection.length > 0
                ? state.sampleConditionSelection
                : [state.activeCondition];
        const wellData = data.wellData;
        const selectedProtocols = data.selectedProtocols;
        for (let k = 0; k < wells.length; k++) {
            nextTick(() => {
                const well = Object.assign(wells[k], wellData);
                dispatch("setActiveCondition", well);
                dispatch("addSample", selectedProtocols);
            });
        }
        commit("setConditionMetadata", wellData);
        commit("endProcessing");
    },

    removeSample({ commit }) {
        commit("removeSample");
    },

    clearSamples({ commit }) {
        commit("clearSamples");
    },

    setSampleEditSelection({ commit }, data) {
        commit("setSampleEditSelection", data);
    },

    editSampleSelection({ commit }, data) {
        // Remove null collectionDate values to prevent overwritting required field
        if (data.collectionDate === null) delete data.collectionDate;
        state.sampleEditSelection.forEach((sample) => {
            commit("editDestinationWell", Object.assign(sample, data));
        });
        commit("clearSampleEditSelection");
        EventBus.$emit("clear_sample_selection");
    },

    updateRequest({ commit }, data) {
        commit("updateRequest", data);
    },

    /**
     * Submits the plate data to the server
     * @param submissionData { dateRequired, location, note, status: "Draft"|"Open" } Data from the SubmitDialog
     * @returns {Promise<AxiosResponse<any>>}
     */

    async submit({ commit, state, getters, dispatch }, submissionData) {
        try {
            commit("beginSaving");

            // Create controls
            const controlsResult = await dispatch("createControls");
            const { controlMapObj, controlIdArray } = controlsResult;

            // Divide plates up into protocols (used for isMultiAssign - see container field 'multi_assign').
            // This works also if isMultiAssign is false, then the key is undefined and the selected protocol(s)
            // on the CreateDialog are used instead.
            const platesByProtocol = _.groupBy(state.plates, "tabId");
            const existingPlatesByProtocol = _.groupBy(state.existingPlates, "tabId");
            const results = [];

            for (const [protocolCode, plates] of Object.entries(platesByProtocol)) {
                // Gather data to send to server
                const samplesPayload = await dispatch("buildSamplesPayload", {
                    plates,
                    controlIdArray,
                    controlMapObj,
                    existingPlatesByProtocol,
                    protocolCode,
                    submissionData,
                });

                // Send data to server
                const samplesResult = await dispatch("submitSamplesToServer", samplesPayload);
                results.push(samplesResult);

                // Call 'requests' store to add the new request
                dispatch(
                    "requests/addRequest",
                    convertRequest(
                        samplesResult,
                        getters.isMultiAssign ? [plates[0].protocol] : state.protocols
                    ),
                    {
                        root: true,
                    }
                );
            }

            // Clear after successful
            commit("resetState");
            return results;
        } catch (err) {
            console.error(err);
            throw err;
        } finally {
            commit("endSaving");
        }
    },

    /**
     * Creates controls for a request
     * @returns {Promise<{controlMapObj: {}, controlIdArray: []}>}
     */
    async createControls({ state }) {
        const controls = state.protocols[0].control_array;
        const functionalTeam = state.protocols[0].functional_team;
        const standardTypes = [
            "Standard",
            "Solvent Blank Before Standard",
            "Solvent Blank After Standard",
            "Blank",
        ];
        const controlMapObj = {};
        const controlIdArray = [];

        if (controls) {
            const fetchConditionTypeMap = await axios.get(`/requests/dropdown_map`, {
                params: { dropdownName: "condition_types", keyByName: true },
            });
            const conditionTypeMap = new Map(fetchConditionTypeMap.data);
            const controlEntityArray = [];

            controls.forEach((control, i) => {
                // Create control condition if it doesn't exist
                if (
                    !control.id &&
                    !(
                        functionalTeam === "Analytical" &&
                        standardTypes.includes(control.control_type)
                    )
                ) {
                    const controlEntity = {
                        schemaId: config.benchlingConditionsEntity,
                        folderId:
                            functionalTeam === "Analytical"
                                ? config.benchlingAnalyticalControlFolder
                                : config.benchlingMolecularControlFolder,
                        name:
                            Math.random().toString(36).substring(2, 15) +
                            Math.random().toString(36).substring(2, 15),
                        fields: {
                            condition_number: {
                                value: i + 1,
                            },
                            condition_name: {
                                value: control.name,
                            },
                            experiment_id: {
                                value:
                                    functionalTeam === "Analytical"
                                        ? config.benchlingAnalyticalControlExperiment
                                        : config.benchlingMolecularControlExperiment,
                            },
                            condition_type: {
                                value: conditionTypeMap.get(control.control_type),
                            },
                            concentration: {
                                value:
                                    control.concentration !== null
                                        ? "" + control.concentration
                                        : null, // Data type text
                            },
                            concentration_units: {
                                value: control.concentration_units || null,
                            },
                        },
                    };
                    controlEntityArray.push(controlEntity);
                }
            });

            // Create control conditions in benchling
            if (controlEntityArray.length > 0) {
                const payload = {
                    entityDataArray: controlEntityArray,
                    entityType: "Condition",
                    namingStrategy: "DELETE_NAMES",
                };
                const controlEntitiesResponse = await axios.post(
                    `/benchling/createAndRegisterCustomEntities`,
                    payload
                );
                const controlEntities = controlEntitiesResponse.data;
                // Extract condition ids
                const conditionIds = _.map(controlEntities, "id");

                // Map conditon ids to well pos
                controls.forEach((control, i) => {
                    if (!control.id && control.well_position_96) {
                        // If Analytical, filter out Standards from controls and push to array
                        if (
                            functionalTeam === "Analytical" &&
                            !standardTypes.includes(control.control_type)
                        ) {
                            control.well_position_96.forEach((wellPos) => {
                                controlIdArray.push(conditionIds[0]);
                            });
                            conditionIds.shift();
                        } else {
                            control.well_position_96.forEach((wellPos) => {
                                controlMapObj[wellPos] = conditionIds[i];
                            });
                        }
                    }
                });
            }
        }

        return { controlMapObj, controlIdArray };
    },

    buildSamplesPayload({ state, getters }, samplesInfo) {
        const {
            plates,
            controlIdArray,
            controlMapObj,
            existingPlatesByProtocol,
            protocolCode,
            submissionData,
        } = samplesInfo;
        const samplesPayload = {
            container_type_id: state.container.id,
            dateRequired: submissionData.dateRequired,
            location_id: _.get(submissionData, "location.id"),
            note: submissionData.note,
            protocols: getters.isMultiAssign
                ? [plates[0].protocol.registry_id]
                : _.map(state.protocols, "registry_id"),
            protocol_template_id: getters.isMultiAssign
                ? plates[0].protocol.template_id
                : state.protocols[0].template_id,
            request: state.request,
            archive_array: [],
        };

        samplesPayload.request.status = submissionData.status;

        if (getters.isTubeSeries && state.isSamplesBool) {
            samplesPayload.samplesOnly = true;
        }

        const experimentIds = [];
        const platesToSubmit = [];
        const existingPlateIds = [];
        // Convert plate layout to a flat array of condition/sample assignments
        for (let plateIndex = 0; plateIndex < plates.length; plateIndex++) {
            const plateMap = [];
            const plate = plates[plateIndex];

            // Check that a plate has at least 1 populated well
            const populatedWells = plate.layout.flat().filter((well) => well.condition);

            // Create array of well plate ids and 'null's for new plates to maintain plate order after adding/removing tabs
            if (populatedWells.length !== 0) existingPlateIds.push(plate.id);

            for (let rowIndex = 0; rowIndex < plate.layout.length; rowIndex++) {
                const row = plate.layout[rowIndex];
                for (let col = 0; col < row.length; col++) {
                    // For each well...
                    const well = row[col];

                    // Get condition from pre-edit plate layout for comparing to submitted plate
                    const existingWell = _.get(existingPlatesByProtocol, [
                        protocolCode,
                        plateIndex,
                        "layout",
                        rowIndex,
                        col,
                    ]);
                    const existingWellCondition = _.get(existingPlatesByProtocol, [
                        protocolCode,
                        plateIndex,
                        "layout",
                        rowIndex,
                        col,
                        "condition",
                    ]);

                    // Helper function to determine how each well has changed
                    const statusCheck = () => {
                        if (
                            existingWellCondition &&
                            well.condition &&
                            !_.isEqual(existingWell, well)
                        )
                            return "update";
                        else if (!existingWellCondition && well.condition) return "create";
                        else if (existingWellCondition && !well.condition) {
                            // Store Well & Sample id for archiving
                            samplesPayload.archive_array.push(
                                existingWell.sampleId,
                                existingWell.wellId
                            );
                            return null;
                        }
                    };

                    // Store experimentIds for REQ metadata
                    if (well.condition && well.condition.experiment_id) {
                        experimentIds.push(well.condition.experiment_id);
                    }

                    // Helper function to determine entity id
                    const assignEntityId = () => {
                        if (well.condition.type !== "Samp") {
                            // Assign condition or control
                            const entityId =
                                well.condition.id ||
                                (getters.isTubeSeries
                                    ? controlIdArray.shift()
                                    : controlMapObj[well.id]);
                            return entityId;
                        } else {
                            // If assigning existing Samp to well, find source entity_id
                            if (well.condition.source_well) return well.condition.source_well;
                            if (well.condition.source_batch) return well.condition.source_batch;
                            if (well.condition.source_condition)
                                return well.condition.source_condition;
                            return well.condition.source_container;
                        }
                    };

                    // Helper function to assign sample id for existing samples
                    const assignSampId = () => {
                        if (well.sampleId) {
                            return well.sampleId;
                        } else if (well.condition.type === "Samp") {
                            return well.condition.id;
                        }
                    };

                    // Helper function to assign sample descriptors
                    const assignSampDescriptors = () => {
                        if (_.isArray(well.sampleDescriptor)) {
                            return _.map(well.sampleDescriptor, "id");
                        } else if (_.get(well, "sampleDescriptor.id")) {
                            return [_.get(well, "sampleDescriptor.id")];
                        } else {
                            return [];
                        }
                    };

                    // Helper function to assign protocols
                    const assignProtocols = () => {
                        if (well.protocols) {
                            return _.map(well.protocols, "registry_id");
                        } else if (_.get(well, "condition.protocols")) {
                            return _.map(well.condition.protocols, "registry_id");
                        } else {
                            return [];
                        }
                    };

                    const wellStatus = statusCheck();
                    if (wellStatus) {
                        // Data object to submit to server
                        const data = {
                            destination_well: well.id,
                            entity_id: assignEntityId(),
                            well_id: well.wellId,
                            plate_id: plate.id,
                            type: well.condition.type,
                            sample_id: assignSampId(),
                            sample_type_id: _.get(well, "sampleType.id"),
                            sample_descriptor_id: assignSampDescriptors(),
                            protocols: assignProtocols(),
                            status: wellStatus,
                            collection_date: well.collectionDate,
                            note: well.note,
                            subwell: _.isEmpty(well.subwell) ? null : _.clone(well.subwell),
                            functional_entity_key_id:
                                well.entityKey && well.entityKey.length > 0
                                    ? well.entityKey.map((key) => key.id)
                                    : [],
                        };

                        plateMap.push(data);
                    }
                }
            }
            if (plateMap.length > 0) platesToSubmit.push(plateMap);
        }

        if (getters.isTubeSeries) {
            samplesPayload.sample_array = platesToSubmit[0]; // Never multiple plates
        } else {
            samplesPayload.plate_map = platesToSubmit;
        }

        // Add map of existing plates with null for newly created
        samplesPayload.existing_plate_ids = existingPlateIds;

        // Add unique list of experiment ids
        samplesPayload.experiment_ids = _.uniq(experimentIds);
        return samplesPayload;
    },

    async submitSamplesToServer({ getters }, samplesPayload) {
        if (getters.isTubeSeries) {
            samplesPayload.taskType = "samplesAndRequest";
            const result = await enqueueBackgroundTaskAndPoll(samplesPayload, 5000, 50);
            if (result.status === "failed") {
                throw new Error(result.more_info.error);
            } else if (result.status !== "completed") {
                // background task did not complete after all retries
                throw new Error(
                    "Samples submission is taking longer than usual. Check the samples page later for your request."
                );
            } else {
                return result.more_info.request;
            }
        } else {
            // call /plate endpoint directly (for now; convert to background task in next pass)
            const result = await axios.post("/samples/plate", samplesPayload);
            return result.data;
        }
    },
};

/******************************************************************
 * MUTATIONS
 ******************************************************************/
const mutations = {
    initializeState: (state, data) => {
        const container = data.container;

        // Initialize first plate using container layout
        if (data.plates) {
            state.plates = data.plates;
            // Save status of plate at initialization
            state.existingPlates = _.cloneDeep(data.plates);
        } else {
            state.plates = [];
        }
        state.collectionDate = data.collectionDate;
        state.container = container;
        state.protocols = data.protocols;
        state.request = data.request || {};
        state.sampleDescriptors = data.sampleDescriptors;
        state.entityKeys = data.entityKeys;
        state.isSamplesBool = data.isSamplesBool;

        // Reduce sample types from Protocols selected
        const uniqueTypes = _.chain(state.protocols)
            .map("material_type_list")
            .flatten()
            .uniq()
            .value();
        if (uniqueTypes.length === 0) {
            state.sampleTypes = data.sampleTypes;
        } else {
            state.sampleTypes = _.filter(data.sampleTypes, function (item) {
                return _.indexOf(uniqueTypes, item.name) > -1;
            });
        }

        state.initialized = true;
    },

    /**
     * Reset state
     */
    resetState: (state) => {
        state.activeCondition = undefined;
        state.collectionDate = undefined;
        state.initialized = false;
        state.isSaving = false;
        state.container = undefined;
        state.experiment = undefined;
        state.protocols = [];
        state.request = {};
        state.sampleDescriptors = [];
        state.sampleTypes = [];
        state.entityKeys = [];
        state.plateWellSelection = [];
        state.existingPlates = [];
        state.sampleConditionSelection = [];
        state.sampleEditSelection = [];
        state.isSamplesBool = false;
    },

    /**
     * Add a new plate / tab (they are tied together)
     * @param state
     * @param options (optional) And object containing
     *  { name: 'Tab Display Name', code: 'protocol code, ie. protocol012', skipSettingActivePlate: true }
     */
    addTab: (state, options = { skipSettingActivePlate: false }) => {
        const isSamples = state.isSamplesBool;
        const isAnalytical = state.container.rows === 1 && state.container.columns === 1;
        const container = state.container;
        const protocols = state.protocols;
        const controls = _.cloneDeep(state.protocols[0].control_array);
        const layout = createPlateLayout(container);
        const standardTypes = [
            "Standard",
            "Solvent Blank Before Standard",
            "Solvent Blank After Standard",
            "Blank",
        ];

        // Add controls
        if (controls) {
            controls.forEach((control, i) => {
                control.well_position_96.forEach((wellPos) => {
                    if (!isSamples && !isAnalytical) {
                        const controlRow = wellPos.substr(0, 1).charCodeAt(0) - 65;
                        const controlCol = parseInt(wellPos.substr(1)) - 1;
                        const well = layout[controlRow][controlCol];
                        const blank = control.control_type === "Blank";
                        if (well && !blank) {
                            well.condition = control;
                            well.sampleType = {
                                id: config.benchlingSampleMaterialTypeControl,
                                name: "control",
                            };
                            well.collectionDate = new Date().toISOString().slice(0, 10);
                            delete well.condition.well_position_96;
                        } else if (well && !well.condition) {
                            well.isBlank = true;
                        }
                    } else if (!isSamples && !standardTypes.includes(control.control_type)) {
                        // Filter Standards from controls
                        layout[0].push({
                            id: "A01",
                            col: layout[0].length,
                            row: 0,
                            condition: control,
                            sampleType: {
                                id: config.benchlingSampleMaterialTypeControl,
                                name: "control",
                            },
                            collectionDate: new Date().toISOString().slice(0, 10),
                            protocols,
                        });
                    }
                });
            });
        }
        state.plates.push({
            tabId: options.protocol ? options.protocol.code : undefined,
            name: options.name,
            protocol: options.protocol,
            layout,
        });
        if (!options.skipSettingActivePlate || state.plates.length === 1) {
            state.activePlate = state.plates.length === 0 ? 0 : state.plates.length - 1;
        }
    },

    removeTab: (state, index) => {
        state.plates.splice(index, 1);
        state.existingPlates.splice(index, 1);
        state.activePlate = index === 0 ? 0 : index - 1;
    },

    togglePlateSelection(state, wellId) {
        const activePlateArray = state.plates[state.activePlate].layout.flat();
        const selectedWell = _.filter(activePlateArray, { id: wellId });
        const isControl = _.get(selectedWell, "0.sampleType.name") === "control";
        const isBlank = _.get(selectedWell, "0.isBlank");
        const selection = state.plateWellSelection;
        const wellIndex = selection.indexOf(wellId);
        if (!isControl && !isBlank) {
            if (wellIndex === -1) {
                selection.push(wellId);
            } else {
                selection.splice(wellIndex, 1);
            }
        }
        state.plateWellSelection = selection;
    },

    expandPlateSelection(state, expId) {
        const activePlateArray = state.plates[state.activePlate].layout.flat();
        const selectionIds = [];
        let selection = state.plateWellSelection;
        let filterArray = [];
        let notify = false;

        // Remove any wells from selection that don't have conditions or are not from the active experiment
        selection.forEach((wellId) => {
            const matchingWell = activePlateArray.find((well) => well.id === wellId);
            if (!matchingWell.condition || matchingWell.condition.experiment_code !== expId) {
                filterArray.push(matchingWell.id);
                notify = true;
            }
        });
        selection = selection.filter((id) => !filterArray.includes(id));

        // Notify user
        if (notify)
            EventBus.$emit("globalAlert", "You may only edit wells for the active experiment.");

        // Collect sampleIds from selected wells
        selection.forEach((wellId) => {
            // Remove any wells from selection that are not from the active experiment
            const matchingWell = activePlateArray.find((well) => well.id === wellId);
            if (matchingWell.sampleId) {
                selectionIds.push(matchingWell.sampleId);
            } else if (matchingWell.condition && matchingWell.condition.type === "Samp") {
                selectionIds.push(matchingWell.condition.id);
            }
        });

        // Find all wells that have matching sampleIds (or for new Wells, have condition ids) to those in the selection
        activePlateArray.forEach((well) => {
            if (
                (well.sampleId && selectionIds.includes(well.sampleId)) ||
                (well.condition && selectionIds.includes(well.condition.id))
            ) {
                if (!selection.includes(well.id)) {
                    selection.push(well.id);
                }
            }
        });
        state.plateWellSelection = selection;
    },

    clearPlateSelection(state) {
        state.plateWellSelection = [];
    },

    selectAllPlateSelection(state) {
        const plateLayout = state.plates[state.activePlate].layout;
        // Filter controls/blanks and map to well.id ('A01', 'B01'...)
        state.plateWellSelection = plateLayout
            .flat()
            .filter((well) => _.get(well, "sampleType.name") !== "control" && !well.isBlank)
            .map((well) => well.id);
    },

    setActivePlate: (state, index) => {
        // Return is active tab is clicked
        if (state.activePlate === index) {
            return;
        }
        state.activePlate = index;

        if (!(state.container && state.container.multi_assign)) {
            state.activeCondition = undefined;
        }
        EventBus.$emit("activePlateChange");
    },

    setActiveCondition: (state, activeCondition) => {
        state.activeCondition = activeCondition;
    },

    setSampleConditionSelection: (state, sampleConditionSelection) => {
        state.sampleConditionSelection = sampleConditionSelection;
    },

    setConditionMetadata: (state, currentMetadata) => {
        state.conditionMetadata = currentMetadata;
    },

    clearConditionMetadata: (state) => {
        state.conditionMetadata = null;
    },

    setExperiment: (state, experiment) => {
        state.experiment = experiment;
    },

    /**
     * Assigns a condition or sample to a well.
     * @param data { row, col }
     */
    assignConditionToDestinationWell: (state, data) => {
        console.log("PCS-assignConditionToDestinationWell");
        console.log("AssignData", data);
        if (state.activeCondition && state.activeCondition.type === "Condition") {
            EventBus.$emit("globalAlert", "Conditions cannot be assigned to a well.");
            return;
        }
        const plate = state.plates[state.activePlate];
        const row = plate.layout[data.row];
        const cellObject = row[data.col];
        if (state.activeCondition) {
            cellObject.condition = _.clone(state.activeCondition);
            cellObject.sampleDescriptor = data.sampleDescriptor;
            cellObject.sampleType = data.sampleType;
            cellObject.entityKey = data.entityKey;
            cellObject.collectionDate = data.collectionDate;
            cellObject.note = data.note;
            cellObject.subwell = _.clone(data.subwell);
            plate.layout[data.row] = row;
        }
    },

    editDestinationWell: (state, data) => {
        console.log("PCS-editDestinationWell");

        const plate = state.plates[state.activePlate];
        const row = plate.layout[data.row];
        const cellObject = row[data.col];

        if (cellObject.condition) {
            if (data.sampleDescriptor) {
                cellObject.sampleDescriptor = _.isArray(data.sampleDescriptor)
                    ? _.uniq(data.sampleDescriptor)
                    : data.sampleDescriptor;
            }
            if (data.sampleType) {
                cellObject.sampleType = data.sampleType;
            }
            if (data.entityKey) {
                cellObject.entityKey = data.entityKey;
            }
            if (data.collectionDate) {
                cellObject.collectionDate = data.collectionDate;
            }
            if (data.note) {
                cellObject.note = data.note;
            }
            if (data.subwell) {
                cellObject.subwell = _.clone(data.subwell);
                cellObject.subwell = _.omitBy(cellObject.subwell, _.isNil);
            }
            if (data.protocols) {
                cellObject.protocols = data.protocols;
            }
        }

        state.plates[state.activePlate].layout[data.row] = row;
    },

    /**
     * Assigns a condition or sample to a well on a specified plate.
     * @param data { well: { row, col }, plate: {...} }
     */
    assignConditionToDestinationPlateAndWell: (state, data) => {
        console.log("PCS-assignConditionToDestinationPlateAndWell");
        const { well, plate, condition } = data;
        const row = plate.layout[well.row];
        const cellObject = row[well.col];

        // If assignAllWells use condition from data object
        if (condition) {
            cellObject.condition = _.clone(condition);
            cellObject.sampleType = condition.sampleType;
            cellObject.sampleDescriptor = condition.sampleDescriptor;
            cellObject.entityKey = condition.entityKey;
            cellObject.collectionDate = condition.collectionDate;
            cellObject.note = condition.note;
            cellObject.subwell = _.clone(condition.subwell);
            plate.layout[well.row] = row;
            state.plates = [...state.plates]; // force reactivity

            // If scanning samples use activeCondition from state
        } else if (state.activeCondition && cellObject) {
            cellObject.condition = _.clone(state.activeCondition);

            // Convert sampleType to sampleType object {name: XXXX, id: XXXX}
            if (cellObject.condition.sample_type) {
                cellObject.sampleType = state.sampleTypes.find((obj) => {
                    return obj.name === cellObject.condition.sample_type;
                });
            }

            // Map sampleDesciptor to sampleDescriptor object {name: XXXX, id: XXXX}
            if (cellObject.condition.sample_descriptor) {
                cellObject.sampleDescriptor = cellObject.condition.sample_descriptor.map(
                    (descriptor) => {
                        return state.sampleDescriptors.find((obj) => {
                            return obj.name === descriptor;
                        });
                    }
                );
                cellObject.sampleDescriptor = _.uniq(cellObject.sampleDescriptor);
            }

            cellObject.entityKey = cellObject.condition.entityKey;

            cellObject.collectionDate = cellObject.condition.collection_date;
            cellObject.note = cellObject.condition.note;
            cellObject.subwell = _.clone(cellObject.condition.subwell);
            plate.layout[well.row] = row;
            state.plates = [...state.plates]; // force reactivity
        } else {
            console.log("No cell object found.", state.activeCondition);
        }
    },

    removeConditionFromDestinationWell: (state, data) => {
        console.log("PCS-removeConditionFromDestinationWell");

        const plate = state.plates[state.activePlate];
        const row = plate.layout[data.row];
        const cellObject = row[data.col];
        if (cellObject.condition) {
            Object.keys(cellObject).forEach((key) => {
                if (!["col", "row", "id"].includes(key)) delete cellObject[key];
            });
            state.plates[state.activePlate].layout[data.row] = row;
        }
    },

    addSample: (state, data) => {
        console.log("PCS-addSample");
        if (state.activeCondition) {
            const plate = state.plates[0];
            const row = plate.layout[0];
            const sampleDescriptor = state.activeCondition.sampleDescriptor;
            const sampleType = state.activeCondition.sampleType;
            const entityKey = state.activeCondition.entityKey;
            const collectionDate = state.activeCondition.collectionDate;
            const protocols = data.selectedProtocols;
            const condition = _.clone(state.activeCondition);
            const note = state.activeCondition.note;
            const subwell = _.clone(state.activeCondition.subwell);

            row.push({
                id: "A01",
                col: row.length,
                row: 0,
                condition,
                sampleDescriptor,
                sampleType,
                collectionDate,
                protocols,
                note,
                subwell,
                entityKey,
            });

            state.plates[0].layout[0] = row;
        }
    },

    removeSample: (state) => {
        console.log("PCS-removeSample");

        const samples = state.sampleEditSelection;
        const removeColArray = _.map(samples, "col");

        let row = state.plates[0].layout[0];
        row = row.filter((sample) => {
            return !removeColArray.includes(sample.col);
        });
        recalcColumnIndex(row);
        state.plates[0].layout[0] = row;
        state.sampleEditSelection = [];
        EventBus.$emit("clear_sample_selection");
    },

    clearSampleConditions: () => {
        state.sampleConditionSelection = [];
    },

    setSampleEditSelection: (state, data) => {
        state.sampleEditSelection = data;
    },

    clearSampleEditSelection: () => {
        state.sampleEditSelection = [];
    },

    clearSamples: (state) => {
        state.plates[0].layout[0] = [];
    },

    beginSaving: (state) => {
        console.log("PCS-beginSaving");
        state.isSaving = true;
    },

    endSaving: (state) => {
        console.log("PCS-endSaving");
        state.isSaving = false;
    },

    beginProcessing: (state) => {
        state.isProcessing = true;
    },

    endProcessing: (state) => {
        state.isProcessing = false;
    },

    updateRequest: (state, data) => {
        state.request = data;
    },
};

function recalcColumnIndex(row) {
    for (let i = 0; i < row.length; i++) {
        row[i].col = i;
    }
}

function convertRequest(request, protocols) {
    console.log(request);
    const protocolId = request.fields["Protocol"].value;
    const protocol = _.find(protocols, { registry_id: protocolId[0] });
    return {
        request_registry_id: request.entityRegistryId,
        request_id: request.id,
        request_url: request.webURL,
        status: request.fields["Status"].displayValue,
        date_required: request.fields["Date Required"].value,
        note: request.fields["Note"].value,
        samples: request.fields["Samples"].value,
        name: protocol.code || protocol.name,
        protocol_url: protocol.url,
    };
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
